import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import RecordRTC from "recordrtc";

const initialState = {
  recordingStatus: "idle",
  mediaBlobUrl: null,
  error: null,
  recorder: null,
  stream: null,
};

export const startRecordingThunk = createAsyncThunk(
  "screenRecording/startRecordingThunk",
  async (_, { dispatch }) => {
    try {
      const audioStream = await navigator.mediaDevices.getUserMedia({
        audio: true,
      });
      const screenStream = await navigator.mediaDevices.getDisplayMedia({
        video: true,
      });

      // Check if entire screen is selected
      const displaySurface = screenStream.getVideoTracks()[0].getSettings().displaySurface;
      if (displaySurface !== 'monitor') {
        throw new Error('Please start screen recording with option entire screen');
      }

      const fullStream = new MediaStream([
        ...screenStream.getTracks(),
        ...audioStream.getTracks(),
      ]);

      const recorder = new RecordRTC(fullStream, {
        type: "video",
        mimeType: "video/webm", // Or 'video/mp4'
      });

      recorder.startRecording();

      dispatch(startRecording());

      return { recorder, stream: fullStream };
    } catch (error) {
      console.error("Error starting recording:", error);
      dispatch(setRecordingError(error.message));
      throw error;
    }
  }
);

export const stopRecordingThunk = createAsyncThunk(
  "screenRecording/stopRecordingThunk",
  async (_, { getState, dispatch }) => {
    try {
      const { recorder, stream } = getState().screenRecording;
      if (recorder) {
        recorder.stopRecording(() => {
          const blob = recorder.getBlob();
          const blobUrl = URL.createObjectURL(blob);
          dispatch(stopRecording(blobUrl));
        });

        if (stream) {
          stream.getTracks().forEach((track) => track.stop());
        }
      }
    } catch (error) {
      console.log("Stop Recording Error: ", error);
    }
  }
);

const screenRecordingSlice = createSlice({
  name: "screenRecording",
  initialState,
  reducers: {
    startRecording(state) {
      state.recordingStatus = "recording";
    },
    stopRecording(state, action) {
      state.recordingStatus = "stopped";
      state.mediaBlobUrl = action.payload;
    },
    clearRecording(state) {
      state.recordingStatus = "idle";
      state.mediaBlobUrl = null;
      state.error = null;
      if (state.stream) {
        state.stream.getTracks().forEach((track) => track.stop());
        state.stream = null;
      }
      state.recorder = null;
    },
    setRecordingError(state, action) {
      state.recordingStatus = "error";
      state.error = action.payload;
    },
  },
});

export const {
  startRecording,
  stopRecording,
  clearRecording,
  setRecordingError,
} = screenRecordingSlice.actions;

export default screenRecordingSlice.reducer;
